import React from 'react';
import './AccountTabView.less';
import ListItem from '../../../../components/ListItem/ListItem';
import { GA_TITLES } from '../../../../../utils';
import { logo } from '../../../../../assets/brandImages';
import config from '../../../../../env.config';
import { Flex, Progress } from 'antd';
import {
  formatIDStatus,
  formatLicenseStatus,
  LocalizePropType,
  UserDataOutput,
  getBrandName,
  isKYCApp,
  formatMoneyEuro,
  formattedAddress,
  isServicesTabEnabled,
  maskSepa,
  isLocalizeAdded,
  isMVV,
  getEmailVerificationStatus,
  isAddressVerificationRequired,
  isDLVerificationRequired,
  isEmailVerificationRequired,
  isIDVerificationRequired,
  isPaymentRequired,
  isPhoneVerificationRequired,
  isTariffEnabled,
  getDepositStatus,
  isPinRequired,
  isFRM,
  useAccountPaymentFlags,
  useProfileWizardContinue,
} from 'getaway-data-layer';
import FileUploadItem from '../../../../components/FileUpload/FileUploadItem';
import BrandIcon from '../../../../components/BrandIcon/BrandIcon';
import Divider from '@mui/material/Divider';
import LanguageSelector from '../../../../components/LanguageSelector/LanguageSelector';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import BrandButton from '../../../../components/BrandButton/BrandButton';

type Props = {
  user: UserDataOutput;
  localize: LocalizePropType;
  phoneNumber: string;
  phoneStatus: string;
  onPressPhoneIncomplete: () => void;
  onPressPhoneCompleted: () => void;
  address: any;
  addressStatus: string;
  onPressAddress: () => void;
  onPressAddressInProgress: () => void;
  onPressAddressCompleted: () => void;
  license: any;
  licenseStatus: string;
  idStatus: string;
  onPressDrivingLicense: () => void;
  onPressDLInProgress: () => void;
  onPressDLCompleted: () => void;
  onOpenPaymentProfile: (payment: any) => void;
  onPressEmail: () => void;
  onPressPin: () => void;
  onPressPassword: () => void;
  onTerminateUser: () => void;
  onLogout: () => void;
  onAddPayment: () => void;
  onClose: () => void;
  onPressCircle: (circle: any) => void;
  onPressJoinCircle: () => void;
  readOnlyMode: boolean;
  onPressID: () => void;
  onPressIDInProgress: () => void;
  onPressIDCompleted: () => void;
  onboardingOnly?: boolean;
  onDeletePayment: (payment: any) => void;
  onAddPaymentProfile: () => void;
  onAddDepositPayment: () => void;
  onDepositBalanceClick: () => void;
  onPressGDPR: () => void;
  blocked: boolean;
  onPressTariff: () => void;
  tariffStatus: string;
  onPressTariffCompleted: () => void;
  emailStatus: string;
  pinStatus: string;
  onPressConsents: () => void;
  onClickVersionHeader: () => void;
  v2OrgSignupEnabled: boolean;
  openOnboardingWizard: () => void;
  separateCreditCheckScreen: boolean;
  inCompleteReadOnlyMode: boolean;
  migratedBubUser: UserDataOutput | null;
  isDeUbqShutdown: boolean;
};

const iconForPayment = (variant: any) => {
  switch (variant) {
    case 'Visa':
      return 'paymentVisa';

    case 'MasterCard':
      return 'paymentMastercard';

    case 'paypal':
      return 'paymentPaypal';

    default:
      return 'visa';
  }
};

const AccountTabView = ({
  user,
  localize,
  phoneNumber,
  phoneStatus,
  onPressPhoneIncomplete,
  onPressPhoneCompleted,
  addressStatus,
  address,
  onPressAddress,
  onPressAddressInProgress,
  onPressAddressCompleted,
  license,
  blocked,
  licenseStatus,
  idStatus,
  onPressDrivingLicense,
  onPressDLInProgress,
  onPressDLCompleted,
  onOpenPaymentProfile,
  onDeletePayment,
  onPressEmail,
  onPressPin,
  onPressPassword,
  onTerminateUser,
  onLogout,
  onAddPayment,
  onPressCircle,
  onPressJoinCircle,
  readOnlyMode,
  onPressID,
  onPressIDInProgress,
  onPressIDCompleted,
  onboardingOnly,
  onAddPaymentProfile,
  onAddDepositPayment,
  onDepositBalanceClick,
  onPressGDPR,
  onPressTariff,
  onPressTariffCompleted,
  tariffStatus,
  emailStatus,
  pinStatus,
  onPressConsents,
  onClickVersionHeader,
  v2OrgSignupEnabled,
  openOnboardingWizard,
  separateCreditCheckScreen,
  inCompleteReadOnlyMode,
  migratedBubUser,
  isDeUbqShutdown,
}: Props) => {
  const {
    showContinueCTA,
    numberOfValidGroups,
    numberOfCompletedGroups,
    progressPercent,
  } = useProfileWizardContinue({
    user,
    readOnly: readOnlyMode,
    separateCreditCheckScreen,
  });

  const isOrg = user?.userType === 'org';
  const tariffDisplayName = isLocalizeAdded(
    localize,
    `settings.tariff.${user?.tariff?.id}.name`
  )
    ? `"${localize(`settings.tariff.${user?.tariff?.id}.name`)}"`
    : `"${user?.tariff?.name}"`;

  const {
    sepaIban,
    isLowSchufaCredit,
    hideAddPayments,
    showDeposit,
    showBlockedPaymentWarning,
    showSepaPendingCWWarning,
    userPayments,
    showFallbackPaymentDialog,
  } = useAccountPaymentFlags({ readOnlyMode });

  const blockDocuments =
    user?.supportedPaymentTypes?.length === 0 && isLowSchufaCredit;

  const isCompanyNameOnly =
    (user?.companyName || user?.address?.company) && !user?.registerNr;

  const readOnlyDialog = (
    <div
      data-testid="AccountTabView-readonlyDialog"
      className="account-warning"
    >
      <div className="account-warning-title">
        {localize(
          isOrg ? 'profile.completed.org.title' : 'profile.completed.title'
        )}
      </div>
      <div className="account-warning-msg">
        {localize(
          isOrg ? 'profile.completed.org.msg' : 'profile.completed.msg'
        )}
      </div>
      {!isServicesTabEnabled() && (
        <button
          className="account-warning-btn"
          onClick={() => {
            window.open(localize('profile.completed.url', '_blank'));
          }}
        >
          {localize('profile.completed.button')}
        </button>
      )}
    </div>
  );

  const orgSection = (
    <>
      <div
        data-testid="AccountTabView-CompanyDetails"
        className="account-header"
      >
        {localize('settings.org.data')}
      </div>
      <div className="account-org-wrapper">
        <img
          src="/images/businessPayment.png"
          style={{
            width: 20,
            height: 20,
            marginRight: 25,
            marginTop: 5,
            objectFit: 'contain',
          }}
        />
        <div
          style={{
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            overflow: 'auto',
            boxSizing: 'border-box',
          }}
        >
          <ListItem
            containerStyles={{ marginTop: 0, paddingTop: 0 }}
            title={
              v2OrgSignupEnabled
                ? `${user?.companyName || user?.address?.company}`
                : `${user?.companyName} (${user?.legalType})`
            }
            readOnly
            disabled={blocked}
            showSeparator={isCompanyNameOnly}
          />
          {user?.registerNr && user?.registerLocation && (
            <ListItem
              title={`${user?.registerNr}, ${user?.registerLocation}`}
              readOnly={readOnlyMode}
              disabled={blocked}
              showSeparator={addressStatus === 'completed'}
            />
          )}
          {addressStatus === 'completed' && (
            <ListItem
              title={
                addressStatus === 'completed' || addressStatus === 'inProgress'
                  ? formattedAddress(address, localize)
                  : localize('settings.login.data.user.add.address.text')
              }
              readOnly={readOnlyMode}
              disabled={blocked}
              showSeparator={false}
            />
          )}
        </div>
      </div>
    </>
  );
  const personalSection = (
    <>
      <div className="account-header">
        {localize(
          isOrg ? 'settings.org.personal.data' : 'settings.personal.data'
        )}
      </div>
      <ListItem
        testid="accountName"
        title={`${user?.firstName} ${user?.lastName}`}
        leftItem={
          <img
            style={{
              width: 20,
              height: 20,
              marginRight: 35,
              objectFit: 'contain',
            }}
            src="/images/user.png"
            alt="user"
          />
        }
        readOnly
        disabled={blocked}
      />
      {isOrg && (
        <ListItem
          title={
            user?.email?.length > 24
              ? `${user?.email?.slice(0, 27)}...`
              : user?.email || ''
          }
          leftIcon="email"
          rightIcon={
            !isEmailVerificationRequired(user)
              ? 'arrowRight'
              : getEmailVerificationStatus(user) === 'completed'
              ? 'arrowRight'
              : 'iconIncomplete'
          }
          rightIconSize={
            !isEmailVerificationRequired(user)
              ? 14
              : getEmailVerificationStatus(user) === 'completed'
              ? 14
              : 10
          }
          brandTextColor={
            isEmailVerificationRequired(user)
              ? ['open', 'inProgress'].includes(emailStatus)
              : false
          }
          onClick={onPressEmail}
          readOnly={readOnlyMode}
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}
      {isPhoneVerificationRequired(user) && (
        <ListItem
          testid="accountPhone"
          title={
            phoneStatus === 'completed'
              ? phoneNumber
              : localize('settings.login.data.user.add.phone.text')
          }
          leftIcon="phone"
          rightIcon={
            phoneStatus === 'completed' ? 'arrowRight' : 'iconIncomplete'
          }
          rightIconSize={phoneStatus === 'completed' ? 14 : 10}
          onClick={
            phoneStatus === 'completed'
              ? onPressPhoneCompleted
              : onPressPhoneIncomplete
          }
          brandTextColor={['open', 'inProgress'].includes(phoneStatus)}
          readOnly={readOnlyMode}
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}
      {isAddressVerificationRequired(user) &&
        !isOrg &&
        !(isKYCApp() && addressStatus === 'open') && (
          <ListItem
            testid="accountAddress"
            title={
              addressStatus === 'completed' || addressStatus === 'inProgress'
                ? formattedAddress(address, localize)
                : localize('settings.login.data.user.add.address.text')
            }
            leftIcon="home"
            rightIcon={
              addressStatus === 'completed'
                ? 'arrowRight'
                : addressStatus === 'inProgress'
                ? 'inProgress'
                : 'iconIncomplete'
            }
            rightIconSize={addressStatus === 'completed' ? 14 : 10}
            onClick={
              addressStatus === 'open'
                ? onPressAddress
                : addressStatus === 'inProgress'
                ? onPressAddressInProgress
                : onPressAddressCompleted
            }
            brandTextColor={addressStatus === 'open'}
            greyTextColor={addressStatus === 'inProgress'}
            readOnly={readOnlyMode}
            disabled={blocked || inCompleteReadOnlyMode}
          />
        )}
      {isIDVerificationRequired(user) && (
        <ListItem
          testid="accountID"
          title={formatIDStatus(idStatus, localize)}
          leftIcon="license"
          rightIcon={
            idStatus === 'completed'
              ? 'arrowRight'
              : idStatus === 'inProgress'
              ? 'inProgress'
              : 'iconIncomplete'
          }
          rightIconSize={idStatus === 'completed' ? 14 : 10}
          onClick={
            ['open', 'deleted'].includes(idStatus) ||
            !user?.identityDocument?.status
              ? onPressID
              : idStatus === 'inProgress'
              ? onPressIDInProgress
              : onPressIDCompleted
          }
          brandTextColor={['open', 'deleted'].includes(idStatus)}
          greyTextColor={idStatus === 'inProgress'}
          readOnly={readOnlyMode}
          disabled={blocked || blockDocuments || inCompleteReadOnlyMode}
        />
      )}
      {isDLVerificationRequired(user) && (
        <ListItem
          testid="accountDL"
          title={formatLicenseStatus(license, licenseStatus, localize)}
          leftIcon="license"
          rightIcon={
            licenseStatus === 'completed'
              ? 'arrowRight'
              : licenseStatus === 'inProgress'
              ? 'inProgress'
              : 'iconIncomplete'
          }
          rightIconSize={licenseStatus === 'completed' ? 14 : 10}
          onClick={
            ['open', 'deleted'].includes(licenseStatus)
              ? onPressDrivingLicense
              : licenseStatus === 'inProgress'
              ? onPressDLInProgress
              : onPressDLCompleted
          }
          brandTextColor={['open', 'deleted'].includes(licenseStatus)}
          greyTextColor={licenseStatus === 'inProgress'}
          readOnly={readOnlyMode}
          disabled={blocked || blockDocuments || inCompleteReadOnlyMode}
        />
      )}
    </>
  );

  const payemntSection = (
    <>
      <div className="account-header">{localize('settings.payment.data')}</div>
      {userPayments &&
        userPayments.map((payment: any) => (
          <div key={payment.id}>
            {payment?.methodType === 'paypal' &&
              payment?.status === 'verified' && (
                <ListItem
                  title={
                    payment?.paypal?.name?.length > 27
                      ? `${payment?.paypal?.name?.slice(0, 27)}...`
                      : payment?.paypal?.name ||
                        localize(`settings.payment.paypal.title`)
                  }
                  leftIcon="pal"
                  leftIconSize={30}
                  rightIcon={
                    user?.blocked
                      ? null
                      : ['pending', 'blocked']?.includes(payment?.status)
                      ? 'inProgress'
                      : 'trash'
                  }
                  rightIconSize={16}
                  onClick={
                    user?.blocked || inCompleteReadOnlyMode
                      ? () => {}
                      : () => onDeletePayment(payment)
                  }
                  readOnly={readOnlyMode}
                />
              )}
            {payment.payIn && (
              <ListItem
                title={localize('settings.payment.payIn.title')}
                leftItem={
                  <img
                    style={{
                      width: 31,
                      height: 20,
                      marginRight: 27,
                      marginLeft: -2,
                      objectFit: 'contain',
                    }}
                    src="/images/paymentPayIn.png"
                    alt="payin"
                  />
                }
                rightIcon={
                  user?.blocked
                    ? null
                    : ['pending', 'blocked'].includes(payment?.status)
                    ? 'inProgress'
                    : 'trash'
                }
                rightIconSize={16}
                onClick={
                  user?.blocked || inCompleteReadOnlyMode
                    ? () => {}
                    : () => onDeletePayment(payment)
                }
                readOnly={readOnlyMode}
              />
            )}
            {payment.sepa && (
              <ListItem
                testid="sepaItem"
                title={maskSepa(payment.sepa.iban)}
                leftIcon="paymentSepa"
                leftIconSize={27}
                rightIcon={
                  user?.blocked
                    ? null
                    : ['pending', 'blocked'].includes(payment?.status)
                    ? 'inProgress'
                    : 'trash'
                }
                rightIconSize={16}
                onClick={
                  user?.blocked || inCompleteReadOnlyMode || readOnlyMode
                    ? () => {}
                    : () => onDeletePayment(payment)
                }
                readOnly={readOnlyMode}
                showSeparator={user?.deposit?.balance === 0}
              />
            )}
            {payment.card && payment.status === 'verified' && (
              <ListItem
                title={`****${payment?.card?.number?.substr(-4)}`}
                leftIcon={iconForPayment(payment?.card?.variant)}
                leftIconSize={
                  iconForPayment(payment?.card?.variant) === 'visa' ? 20 : 27
                }
                rightIcon={
                  user?.blocked
                    ? null
                    : ['pending', 'blocked']?.includes(payment?.status)
                    ? 'inProgress'
                    : 'trash'
                }
                rightIconSize={16}
                onClick={
                  user?.blocked ? () => {} : () => onDeletePayment(payment)
                }
                readOnly={readOnlyMode}
              />
            )}
          </div>
        ))}
      {user?.deposit?.balance > 0 && getDepositStatus(user) === 'completed' && (
        <>
          <ListItem
            testid="accountPayment"
            containerStyles={{ paddingTop: 0, marginTop: 0 }}
            title={localize('deposit.balance.title')}
            leftItem={<div style={{ width: 56 }} />}
            value={
              <div className="deposit-balance">
                {formatMoneyEuro(user?.deposit?.balance)}
              </div>
            }
            rightIcon="arrowRight"
            rightIconSize={16}
            onClick={() => onDepositBalanceClick()}
            disabled={blocked || inCompleteReadOnlyMode}
          />
        </>
      )}
      {!hideAddPayments && (
        <ListItem
          testid="accountPayment"
          title={localize('settings.payment.data.add')}
          brandTextColor
          leftIcon="plus"
          leftIconSize={14}
          rightIcon={
            userPayments?.length &&
            userPayments?.find((payment: any) => payment.status === 'verified')
              ? 'arrowRight'
              : 'iconIncomplete'
          }
          rightIconSize={
            userPayments?.length &&
            userPayments?.find((payment: any) => payment.status === 'verified')
              ? 14
              : 10
          }
          onClick={onAddPayment}
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}

      {showFallbackPaymentDialog && (
        <div
          className="profile-account-warning"
          data-testid="AccountTabView-fallbackPaymentWarning"
        >
          <div className="profile-account-warning-msg">
            {localize('account.low.schufa.desc')}
          </div>
        </div>
      )}
      {showDeposit && (
        <>
          <div
            data-testid="AccountTabView-DeopsitWarning"
            className="account-deposit-warning"
          >
            <ListItem
              containerStyles={{ margin: 0 }}
              title={maskSepa(sepaIban)}
              leftIcon="paymentSepa"
              leftIconSize={27}
              rightIcon="inProgress"
              rightIconSize={16}
              readOnly={readOnlyMode}
            />

            <div className="account-deposit-warning-msg">
              <Markdown rehypePlugins={[rehypeRaw]}>
                {localize('account-deposit.warning.desc')}
              </Markdown>
            </div>
            <div className="account-deposit-warning-container">
              <button
                data-testid="AccountTabView-payDeopsitBtn"
                className="account-deposit-warning-btn"
                onClick={onAddDepositPayment}
              >
                {localize('account-deposit.warning.btn')}
              </button>
              <div className="account-deposit-warning-value">
                {formatMoneyEuro(user?.deposit?.toPayForSepaUnlock)}
              </div>
            </div>
          </div>
        </>
      )}
      {isLowSchufaCredit && (
        <div
          data-testid="AccountTabView-lowSchufaWarning"
          className="account-deposit-warning"
        >
          <ListItem
            containerStyles={{ margin: 0, marginTop: 12 }}
            title={maskSepa(sepaIban)}
            leftIcon="paymentSepa"
            leftIconSize={27}
            rightIcon="inProgress"
            rightIconSize={16}
            readOnly={readOnlyMode}
          />

          <div className="account-deposit-warning-msg">
            {localize('account.low.schufa.desc')}
          </div>
        </div>
      )}
      {showSepaPendingCWWarning && (
        <div className="account-deposit-warning">
          <ListItem
            containerStyles={{ margin: 0, marginTop: 12 }}
            title={maskSepa(sepaIban)}
            leftIcon="paymentSepa"
            leftIconSize={27}
            rightIcon="inProgress"
            rightIconSize={16}
            readOnly={readOnlyMode}
          />

          <div className="account-deposit-warning-msg">
            {localize('account.org.pending.payment.note')}
          </div>
        </div>
      )}
      {showBlockedPaymentWarning && (
        <div
          data-testid="AccountTabView-depositWarning"
          className="account-deposit-warning"
        >
          <ListItem
            containerStyles={{ margin: 0, marginTop: 12 }}
            title={maskSepa(sepaIban)}
            leftIcon="paymentSepa"
            leftIconSize={27}
            rightIcon="inProgress"
            rightIconSize={16}
            readOnly={readOnlyMode}
          />

          <div className="account-deposit-warning-msg">
            {localize('account.payment.blocked.desc')}
          </div>
        </div>
      )}
    </>
  );

  const payemntProfilesSection = (
    <>
      <div className="account-header">{localize('settings.payment.data')}</div>
      {userPayments &&
        userPayments.map((payment: any) => (
          <div key={payment.id}>
            {payment?.methodType === 'paypal' &&
              payment?.status === 'verified' && (
                <ListItem
                  title={localize(
                    payment?.billingInfo?.billingProfile ||
                      payment?.address?.billingProfile
                      ? `profile.payment.type.${
                          payment?.billingInfo?.billingProfile ||
                          payment?.address?.billingProfile
                        }`
                      : 'profile.payment.type.personal'
                  )}
                  descriptionComponent={
                    <div className="account-payment-description">
                      <BrandIcon
                        style={{
                          width: 33,
                          height: 25,
                          marginRight: 10,
                        }}
                        name="pal"
                      />
                      <div>
                        {payment?.paypal?.name?.length > 15
                          ? `${payment?.paypal?.name?.slice(0, 15)}...`
                          : payment?.paypal?.name ||
                            localize(`settings.payment.paypal.title`)}
                      </div>
                    </div>
                  }
                  leftItem={
                    <img
                      style={{
                        width: 31,
                        height: 20,
                        marginRight: 35,
                        objectFit: 'contain',
                      }}
                      src={
                        payment?.billingInfo?.billingProfile ||
                        payment?.address?.billingProfile
                          ? payment?.billingInfo?.billingProfile ===
                              'personal' ||
                            payment?.address?.billingProfile === 'personal'
                            ? '/images/privatePayment.png'
                            : '/images/businessPayment.png'
                          : '/images/privatePayment.png'
                      }
                      alt="payin"
                    />
                  }
                  rightIcon={
                    ['pending', 'blocked'].includes(payment?.status) &&
                    !user?.blocked
                      ? 'inProgress'
                      : 'arrowRight'
                  }
                  rightIconSize={16}
                  showStandard={
                    !isOrg &&
                    (userPayments?.length === 1 ||
                      (payment?.defaultOne !== undefined &&
                        payment?.defaultOne))
                  }
                  onClick={() => onOpenPaymentProfile(payment)}
                  readOnly={readOnlyMode}
                />
              )}
            {payment.payIn && (
              <ListItem
                title={localize(
                  payment?.billingInfo?.billingProfile ||
                    payment?.address?.billingProfile
                    ? `profile.payment.type.${
                        payment?.billingInfo?.billingProfile ||
                        payment?.address?.billingProfile
                      }`
                    : 'profile.payment.type.personal'
                )}
                descriptionComponent={
                  <div className="account-payment-description">
                    <div>{localize('settings.payment.payIn.title')}</div>
                  </div>
                }
                leftItem={
                  <img
                    style={{
                      width: 31,
                      height: 20,
                      marginRight: 35,
                      objectFit: 'contain',
                    }}
                    src={
                      payment?.billingInfo?.billingProfile ||
                      payment?.address?.billingProfile
                        ? payment?.billingInfo?.billingProfile === 'personal' ||
                          payment?.address?.billingProfile === 'personal'
                          ? '/images/privatePayment.png'
                          : '/images/businessPayment.png'
                        : '/images/privatePayment.png'
                    }
                    alt="payin"
                  />
                }
                rightIcon={
                  ['pending', 'blocked'].includes(payment?.status) &&
                  !user?.blocked
                    ? 'inProgress'
                    : 'arrowRight'
                }
                rightIconSize={16}
                showStandard={
                  userPayments?.length === 1 ||
                  (payment?.defaultOne !== undefined && payment?.defaultOne)
                }
                onClick={() => onOpenPaymentProfile(payment)}
                readOnly={readOnlyMode}
              />
            )}
            {payment.sepa && (
              <ListItem
                testid="sepaItem"
                title={localize(
                  isOrg
                    ? 'profile.payment.type.business'
                    : payment?.billingInfo?.billingProfile ||
                      payment?.address?.billingProfile
                    ? `profile.payment.type.${
                        payment?.billingInfo?.billingProfile ||
                        payment?.address?.billingProfile
                      }`
                    : 'profile.payment.type.personal'
                )}
                descriptionComponent={
                  <div className="account-payment-description">
                    <BrandIcon
                      style={{
                        width: 33,
                        height: 25,
                        marginRight: 10,
                      }}
                      name="paymentSepa"
                    />
                    <div>{maskSepa(payment.sepa.iban)}</div>
                  </div>
                }
                leftItem={
                  <img
                    style={{
                      width: 31,
                      height: 20,
                      marginRight: 35,
                      objectFit: 'contain',
                    }}
                    src={
                      isOrg
                        ? '/images/businessPayment.png'
                        : payment?.billingInfo?.billingProfile ||
                          payment?.address?.billingProfile
                        ? payment?.billingInfo?.billingProfile === 'personal' ||
                          payment?.address?.billingProfile === 'personal'
                          ? '/images/privatePayment.png'
                          : '/images/businessPayment.png'
                        : '/images/privatePayment.png'
                    }
                    alt="payin"
                  />
                }
                rightIcon={
                  isOrg
                    ? 'trash'
                    : ['pending', 'blocked'].includes(payment?.status) &&
                      !user?.blocked
                    ? 'inProgress'
                    : 'arrowRight'
                }
                rightIconSize={16}
                showStandard={
                  !isOrg &&
                  (userPayments?.length === 1 ||
                    (payment?.defaultOne !== undefined && payment?.defaultOne))
                }
                onClick={() => onOpenPaymentProfile(payment)}
                readOnly={readOnlyMode}
              />
            )}
            {payment.card && payment.status === 'verified' && (
              <ListItem
                title={localize(
                  payment?.billingInfo?.billingProfile ||
                    payment?.address?.billingProfile
                    ? `profile.payment.type.${
                        payment?.billingInfo?.billingProfile ||
                        payment?.address?.billingProfile
                      }`
                    : 'profile.payment.type.personal'
                )}
                descriptionComponent={
                  <div className="account-payment-description">
                    <BrandIcon
                      style={{
                        width:
                          iconForPayment(payment?.card?.variant) === 'visa'
                            ? 20
                            : 33,
                        height: 25,
                        marginRight: 10,
                      }}
                      name={iconForPayment(payment?.card?.variant)}
                    />

                    <div>{`****${payment?.card?.number?.substr(-4)}`}</div>
                  </div>
                }
                leftItem={
                  <img
                    style={{
                      width: 31,
                      height: 20,
                      marginRight: 35,
                      objectFit: 'contain',
                    }}
                    src={
                      payment?.billingInfo?.billingProfile ||
                      payment?.address?.billingProfile
                        ? payment?.billingInfo?.billingProfile === 'personal' ||
                          payment?.address?.billingProfile === 'personal'
                          ? '/images/privatePayment.png'
                          : '/images/businessPayment.png'
                        : '/images/privatePayment.png'
                    }
                    alt="payin"
                  />
                }
                rightIcon={
                  ['pending', 'blocked']?.includes(payment?.status)
                    ? 'inProgress'
                    : 'arrowRight'
                }
                rightIconSize={16}
                showStandard={
                  userPayments?.length === 1 ||
                  (payment?.defaultOne !== undefined && payment?.defaultOne)
                }
                onClick={() => onOpenPaymentProfile(payment)}
                readOnly={readOnlyMode}
              />
            )}
          </div>
        ))}
      {user?.deposit?.balance > 0 && getDepositStatus(user) === 'completed' && (
        <ListItem
          title={localize('deposit.balance.title')}
          leftItem={
            <img
              style={{
                width: 31,
                height: 20,
                marginRight: 27,
                marginLeft: -2,
                objectFit: 'contain',
              }}
              src="/images/paymentPayIn.png"
              alt="payin"
            />
          }
          value={
            <div className="deposit-balance">
              {formatMoneyEuro(user?.deposit?.balance)}
            </div>
          }
          rightIcon="arrowRight"
          rightIconSize={16}
          onClick={() => onDepositBalanceClick()}
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}
      {showDeposit && (
        <>
          <div className="account-deposit-warning">
            <ListItem
              containerStyles={{ margin: 0 }}
              title={maskSepa(sepaIban)}
              leftIcon="paymentSepa"
              leftIconSize={27}
              rightIcon="inProgress"
              rightIconSize={16}
              readOnly={readOnlyMode}
            />

            <div className="account-deposit-warning-msg">
              <Markdown rehypePlugins={[rehypeRaw]}>
                {localize('account-deposit.warning.desc')}
              </Markdown>
            </div>
            <div className="account-deposit-warning-container">
              <button
                className="account-deposit-warning-btn"
                onClick={onAddDepositPayment}
              >
                {localize('account-deposit.warning.btn')}
              </button>
              <div className="account-deposit-warning-value">
                {formatMoneyEuro(user?.deposit?.toPayForSepaUnlock)}
              </div>
            </div>
          </div>
        </>
      )}
      {isLowSchufaCredit && (
        <div className="account-deposit-warning">
          <ListItem
            containerStyles={{ margin: 0, marginTop: 12 }}
            title={maskSepa(sepaIban)}
            leftIcon="paymentSepa"
            leftIconSize={27}
            rightIcon="inProgress"
            rightIconSize={16}
            readOnly={readOnlyMode}
          />

          <div className="account-deposit-warning-msg">
            {localize('account.low.schufa.desc')}
          </div>
        </div>
      )}
      {showSepaPendingCWWarning && (
        <div
          className="account-deposit-warning"
          data-testid="AccountTabView-sepaPendingCWWarning"
        >
          <ListItem
            containerStyles={{ margin: 0, marginTop: 12 }}
            title={maskSepa(sepaIban)}
            leftIcon="paymentSepa"
            leftIconSize={27}
            rightIcon="inProgress"
            rightIconSize={16}
            readOnly={readOnlyMode}
          />

          <div className="account-deposit-warning-msg">
            {localize('account.org.pending.payment.note')}
          </div>
        </div>
      )}
      {showBlockedPaymentWarning && (
        <div
          data-testid="AccountTabView-depositWarning"
          className="account-deposit-warning"
        >
          <ListItem
            containerStyles={{ margin: 0, marginTop: 12 }}
            title={maskSepa(sepaIban)}
            leftIcon="paymentSepa"
            leftIconSize={27}
            rightIcon="inProgress"
            rightIconSize={16}
            readOnly={readOnlyMode}
          />

          <div className="account-deposit-warning-msg">
            {localize('account.payment.blocked.desc')}
          </div>
        </div>
      )}
      {!hideAddPayments && (
        <ListItem
          testid="accountPayment"
          title={localize('settings.payment.profile.add')}
          brandTextColor
          leftIcon="plus"
          leftIconSize={14}
          rightIcon={
            userPayments?.length &&
            userPayments?.find((payment: any) => payment.status === 'verified')
              ? 'arrowRight'
              : 'iconIncomplete'
          }
          rightIconSize={
            userPayments?.length &&
            userPayments?.find((payment: any) => payment.status === 'verified')
              ? 14
              : 10
          }
          onClick={
            isOrg || user?.address?.billingProfile === 'business'
              ? onAddPayment
              : onAddPaymentProfile
          }
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}
      {showFallbackPaymentDialog && (
        <div className="profile-account-warning">
          <div className="profile-account-warning-msg">
            {localize('account.low.schufa.desc')}
          </div>
        </div>
      )}
    </>
  );

  const circleSection = !readOnlyMode && (
    <>
      <div className="account-header">{localize('settings.circles.data')}</div>

      {user?.circles &&
        user?.circles?.length > 0 &&
        user?.circles.map((circle: any) => {
          if (circle.circleType === 'brand') {
            return null;
          }
          return (
            <ListItem
              testid={`circleName-${circle.circleCode}`}
              key={circle.id}
              title={
                circle.circleType === 'brand'
                  ? localize(
                      // @ts-ignore
                      `brand.${getBrandName(config.brand.xBrandId)}.name`
                    )
                  : circle.name
              }
              rightIcon="arrowRight"
              rightIconSize={14}
              leftItem={
                <img
                  style={{
                    width: 40,
                    height: 40,
                    marginRight: 16,
                    objectFit: 'contain',
                  }}
                  src={circle.circleType === 'brand' ? logo : circle?.logo}
                  alt="circle-logo"
                />
              }
              onClick={() => onPressCircle(circle)}
              disabled={blocked || inCompleteReadOnlyMode}
            />
          );
        })}
      {!readOnlyMode && (
        <ListItem
          testid="circle"
          title={localize('settings.circles.data.add')}
          brandTextColor
          leftIcon="plus"
          leftIconSize={14}
          rightIcon="arrowRightBrand"
          rightIconSize={14}
          onClick={onPressJoinCircle}
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}
    </>
  );

  const loginSection = (
    <>
      <div className="account-header">{localize('settings.login.data')}</div>
      {isTariffEnabled(user) && (
        <ListItem
          testid="accountTariff"
          title={
            user?.tariff?.name
              ? localize('settings.tariff', tariffDisplayName)
              : localize('settings.select.tariff')
          }
          leftItem={
            <img
              style={{
                width: 20,
                height: 20,
                marginRight: 35,
                objectFit: 'contain',
              }}
              src="/images/docs.png"
              alt="tarif"
            />
          }
          rightIconSize={tariffStatus === 'completed' ? 14 : 10}
          rightIcon={
            tariffStatus === 'completed' ? 'arrowRight' : 'iconIncomplete'
          }
          onClick={
            tariffStatus === 'completed' && !!(isMVV() ? user?.extraData : true)
              ? onPressTariffCompleted
              : onPressTariff
          }
          brandTextColor={
            ['open', 'inProgress'].includes(tariffStatus) ||
            (isMVV() && !user?.extraData)
          }
          disabled={blocked || inCompleteReadOnlyMode}
          readOnly={readOnlyMode}
        />
      )}
      {!isOrg && (
        <ListItem
          testid="accountEmail"
          title={
            user?.email?.length > 24
              ? `${user?.email?.slice(0, 27)}...`
              : user?.email || ''
          }
          leftIcon="email"
          rightIcon={
            !isEmailVerificationRequired(user)
              ? 'arrowRight'
              : getEmailVerificationStatus(user) === 'completed'
              ? 'arrowRight'
              : 'iconIncomplete'
          }
          rightIconSize={
            !isEmailVerificationRequired(user)
              ? 14
              : getEmailVerificationStatus(user) === 'completed'
              ? 14
              : 10
          }
          brandTextColor={
            isEmailVerificationRequired(user)
              ? ['open', 'inProgress'].includes(emailStatus)
              : false
          }
          onClick={onPressEmail}
          readOnly={readOnlyMode}
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}
      {(isPinRequired(user) || user?.pin) && (
        <ListItem
          testid="pin"
          title={localize('settings.login.data.pin')}
          leftIcon="pin"
          rightIcon={
            isPinRequired(user)
              ? pinStatus === 'completed'
                ? 'arrowRight'
                : 'iconIncomplete'
              : 'arrowRight'
          }
          rightIconSize={
            isPinRequired(user) ? (pinStatus === 'completed' ? 14 : 10) : 14
          }
          brandTextColor={
            isPinRequired(user)
              ? ['open', 'inProgress'].includes(pinStatus)
              : false
          }
          onClick={onPressPin}
          readOnly={readOnlyMode}
          disabled={blocked || inCompleteReadOnlyMode}
        />
      )}
      {!readOnlyMode && (
        <ListItem
          title={localize('settings.login.data.password')}
          leftIcon="key"
          rightIcon="arrowRight"
          rightIconSize={14}
          onClick={onPressPassword}
          readOnly={readOnlyMode}
        />
      )}
      <ListItem
        testid="gdpr"
        title={localize('settings.gdpr')}
        leftItem={
          <img
            style={{
              width: 20,
              height: 20,
              marginRight: 35,
              objectFit: 'contain',
            }}
            src="/images/gdpr.png"
            alt="user"
          />
        }
        rightIcon="arrowRight"
        rightIconSize={14}
        onClick={onPressGDPR}
        disabled={blocked}
      />
      <ListItem
        testid="consents"
        title={localize('settings.consents')}
        leftItem={
          <img
            style={{
              width: 20,
              height: 20,
              marginRight: 35,
              objectFit: 'contain',
            }}
            src="/images/rule.png"
            alt="user"
          />
        }
        rightIcon="arrowRight"
        rightIconSize={14}
        onClick={onPressConsents}
        disabled={blocked || inCompleteReadOnlyMode}
      />
      {!readOnlyMode && <FileUploadItem localize={localize} />}
    </>
  );
  return (
    <>
      {!isDeUbqShutdown && !migratedBubUser && readOnlyMode && readOnlyDialog}
      {(isOrg || user?.address?.billingProfile === 'business') && orgSection}
      {personalSection}
      {isPaymentRequired(user) &&
        (isKYCApp() &&
        !isOrg &&
        !(
          user?.address?.billingProfile &&
          user?.address?.billingProfile === 'business'
        )
          ? payemntSection
          : payemntProfilesSection)}
      {!onboardingOnly && circleSection}
      {loginSection}
      <>
        <div
          id={GA_TITLES.AUTH.LOGOUT}
          className="account-item"
          onClick={onLogout}
        >
          {localize('settings.logout')}
        </div>
        <Divider />
        {!readOnlyMode && (
          <>
            <div className="account-item-terminate" onClick={onTerminateUser}>
              {localize('settings.login.data.terminate')}
            </div>
            <Divider />
          </>
        )}
        <div className="user-bottom">
          <div
            className="user-version-header"
            onClick={onClickVersionHeader}
            style={{ color: '#808080' }}
          >
            {localize('home.about')}
          </div>
          <LanguageSelector />
        </div>
      </>
      <div className="screen-vertical-space" />
      {!readOnlyMode && showContinueCTA && (
        <BrandButton
          id={GA_TITLES.DASHBOARD.WIZARD_CONTINUE}
          title={localize('profile.wizard.continue')}
          onClick={openOnboardingWizard}
          rightComponent={
            <Flex
              wrap
              gap="middle"
              style={{ position: 'absolute', right: 10, bottom: 4 }}
            >
              <Progress
                type="dashboard"
                steps={{ count: numberOfValidGroups, gap: 10 }}
                percent={progressPercent}
                width={30}
                strokeColor="#ffffff"
                trailColor={isMVV() || isFRM() ? 'grey' : 'lightgrey'}
                strokeWidth={8}
                format={() => (
                  <span
                    style={{ color: isMVV() || isFRM() ? 'grey' : 'white' }}
                  >
                    {numberOfCompletedGroups}/{numberOfValidGroups}
                  </span>
                )}
              />
            </Flex>
          }
        />
      )}
    </>
  );
};

export default AccountTabView;
